import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

import history from '../../history'
import VideoList from './VideoList'
import VideoAi from './VideoAi'
import BrandOnboarding from './components/BrandOnboarding'
import {
  checkAccess,
  getAiVideoAccessInfo
} from '../../services/api/aiVideoGeneration'
import NotFound from '../NotFound'
import { Spin } from 'antd'

const normalizeStatus = status => String(status || '').toLowerCase()

const setBrandSessionRoute = sessionId => {
  if (!sessionId) return

  const params = new URLSearchParams(window.location.search)
  if (params.get('brandSessionId') === String(sessionId)) return

  params.set('brandSessionId', sessionId)
  const queryString = params.toString()
  history.replace(`/video-create${queryString ? `?${queryString}` : ''}`)
}

const isAdvancedVideoCreationMode = video =>
  (video?.video_creation_mode || video?.videoCreationMode) === 'advanced'

const VideoAiHandler = props => {
  const {
    match: {
      params: { id, step }
    }
  } = props

  const [isEdit, setIsEdit] = useState(!!id)
  const [videoId, setVideoId] = useState(id || null)
  const [initialStep, setInitialStep] = useState(step || null)
  const [hasAccess, setHasAccess] = useState(null)
  const [brandOnboarding, setBrandOnboarding] = useState(null)
  const [showBrandOnboarding, setShowBrandOnboarding] = useState(false)
  const [skippedOnboarding, setSkippedOnboarding] = useState(false)

  const userInfo = useSelector(state => state.user.info)
  const organizationId =
    userInfo?.active_organization_id ||
    userInfo?.activeOrganizationId ||
    userInfo?.organization_id ||
    userInfo?.organization?.id ||
    userInfo?.organization?.organization_id

  useEffect(() => {
    checkUserAccess()
  }, [])

  useEffect(() => {
    if (id) {
      setIsEdit(true)
      setVideoId(id)
      setInitialStep(step || null)
    } else {
      setIsEdit(false)
      setVideoId(null)
      setInitialStep(null)
    }
  }, [id, step])

  const checkUserAccess = async () => {
    try {
      const res = await checkAccess()
      const accessInfo = getAiVideoAccessInfo(res)
      const onboardingStatus = normalizeStatus(accessInfo.onboarding?.status)

      setBrandOnboarding(accessInfo.onboarding)
      setShowBrandOnboarding(
        Boolean(
          accessInfo.hasAccess &&
            accessInfo.onboarding?.id &&
            onboardingStatus &&
            onboardingStatus !== 'completed'
        )
      )
      setHasAccess(accessInfo.hasAccess)
    } catch (error) {
      console.error('Failed to check user access:', error)
      setHasAccess(false)
    }
  }

  const handleEditNavigation = (video, step = 'uploadDocument') => {
    const videoId = video.id
    const resolvedStep =
      step === 'creativeDirection' && !isAdvancedVideoCreationMode(video)
        ? 'shotList'
        : step

    history.push(`/video-create/${videoId}/${resolvedStep}`)
  }

  const handleCreateNew = () => {
    setIsEdit(true)
    setVideoId(null)
    setInitialStep(null)
  }

  const handleBrandOnboardingComplete = onboardingResult => {
    setBrandOnboarding(prev =>
      ({
        ...(prev || {}),
        id: prev?.id || onboardingResult?.sessionId,
        status: 'completed'
      })
    )
    setShowBrandOnboarding(false)
  }

  const handleGoToVideoList = () => {
    setIsEdit(false)
    setVideoId(null)
    setInitialStep(null)
    setShowBrandOnboarding(false)
    setSkippedOnboarding(true)
    history.push('/video-create')
  }

  const handleEditOnboarding = () => {
    setIsEdit(false)
    setVideoId(null)
    setInitialStep(null)
    setShowBrandOnboarding(true)
    setSkippedOnboarding(false)
    setBrandSessionRoute(brandOnboarding?.id)
  }

  // Loading state while checking access
  if (hasAccess === null) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '60vh',
          width: '100%'
        }}
      >
        <Spin tip="Checking Access..." size="large" />
      </div>
    )
  }

  if (!hasAccess) {
    return (
      <NotFound text="You don't have access to this feature. Please contact your administrator." />
    )
  }

  const onboardingSessionId = brandOnboarding?.id || null
  const onboardingStatus = normalizeStatus(brandOnboarding?.status)
  const isBrandOnboardingComplete = onboardingStatus === 'completed'
  const shouldShowBrandOnboarding =
    !id &&
    !skippedOnboarding &&
    (showBrandOnboarding || !isBrandOnboardingComplete)

  if (shouldShowBrandOnboarding) {
    if (!organizationId) {
      return (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '60vh',
            width: '100%'
          }}
        >
          <Spin tip="Preparing organization..." size="large" />
        </div>
      )
    }

    return (
      <BrandOnboarding
        organizationId={organizationId}
        initialSessionId={onboardingSessionId}
        shouldCreateSession={!onboardingSessionId}
        onComplete={handleBrandOnboardingComplete}
        onGoToVideoList={handleGoToVideoList}
      />
    )
  }

  if (isEdit) {
    return (
      <VideoAi
        videoId={videoId}
        initialStep={initialStep}
        handleGoBack={() => {
          if (id) {
            history.push('/video-create')
          } else {
            setIsEdit(false)
          }
        }}
      />
    )
  }

  return (
    <VideoList
      onCreateNew={handleCreateNew}
      onEditVideo={handleEditNavigation}
      onEditOnboarding={handleEditOnboarding}
    />
  )
}

export default VideoAiHandler
